// src/pages/offlineSales/EditOfflineSalesOrderPage.jsx
//
// Edit an existing Offline Sales order — full parity with CreateOfflineSalesOrderPage.jsx:
// shop, warehouse, customer, fulfillment method, delivery details, items, payments, and
// notes are all editable while the order's current status is still open
// (OrderStatus.isClosed === false).
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, ShoppingCart, X, Store, User, Package, CreditCard,
  FileText, Truck, MapPin, CheckCircle2, UserCheck,
} from 'lucide-react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { usePermissions } from '../../hooks/usePermissions';
import useOfflineSalesForm from '../../hooks/useOfflineSalesForm';
import { getOrder, uploadOrderPaymentProof, getSalesReps } from '../../services/api/offlineSales/orders';
import { getPaymentMethods } from '../../services/api/offlineSales/paymentMethods';
import { getShops } from '../../services/api/shops';
import { getUserWarehouses } from '../../services/api/packingBatch';
import { getCities } from '../../services/api/orderReference';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import PaymentMethodPicker from '../../components/ui/PaymentMethodPicker/PaymentMethodPicker';
import PaymentRecordEntry from '../../components/common/PaymentRecordEntry/PaymentRecordEntry';
import CustomerSelectorModal from './components/CustomerSelectorModal';
import CityDeliverySection from '../orders/components/shared/CityDeliverySection';
import DeliveryChargeWeightSection from '../orders/components/shared/DeliveryChargeWeightSection';
import OfflineSalesItemsSection from './components/OfflineSalesItemsSection';
import { toSalesRepOptions } from './salesRepOptions';
import styles from './CreateOfflineSalesOrderPage.module.css';

const FULFILLMENT_METHODS = [
  { key: 'counter_pickup', icon: Store, labelKey: 'counterPickup' },
  { key: 'self_delivery', icon: Package, labelKey: 'selfDelivery' },
  { key: 'courier', icon: Truck, labelKey: 'courier' },
];


const EditOfflineSalesOrderPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { t, formatCurrency } = useLocalization();
  const { success, error } = useAlert();
  const { hasPermission } = usePermissions();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [shops, setShops] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [cities, setCities] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    shopId, customer, items, warehouseId, fulfillmentMethod, orderDiscount, notes, paymentDetails, salesRepUserId,
    cityId, weight, address, isCalculatingDelivery, errors,
    setShopId, setCustomer, setWarehouseId, setFulfillmentMethod, setOrderDiscount, setNotes, setSalesRepUserId,
    setCityId, setWeight, setAddress, setDeliveryChargeOverride,
    addItem, updateItem, removeItem,
    addPaymentDetail, updatePaymentDetail, removePaymentDetail,
    computeTotals, editOrder, submitting,
  } = useOfflineSalesForm(order);

  const selectedShop = shops.find(s => s.id === shopId) || null;
  const totals = computeTotals(selectedShop);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [orderData, methodsData, shopsData, warehouseData, citiesData, repsData] = await Promise.all([
          getOrder(orderId),
          getPaymentMethods().catch(() => []),
          getShops({ assigned: true, type: 'pos' }).catch(() => []),
          getUserWarehouses().catch(() => ({ warehouses: [] })),
          getCities().catch(() => ({ cities: [] })),
          getSalesReps().catch(() => ({ salesReps: [] })),
        ]);
        if (orderData.OrderStatus?.isClosed) {
          error(t('offlineSales.orderNotEditable') || 'This order can no longer be edited');
          navigate(`/offline-sales/orders/${orderId}`);
          return;
        }
        setOrder(orderData);
        setPaymentMethods(Array.isArray(methodsData) ? methodsData : []);
        setShops(Array.isArray(shopsData) ? shopsData : []);
        setWarehouses(warehouseData?.warehouses || []);
        setCities(citiesData?.cities || []);
        // Keep the current rep selectable even if they're no longer in a sales-rep role
        const reps = repsData?.salesReps || [];
        if (orderData.salesRep && !reps.some(r => r.id === orderData.salesRep.id)) reps.push(orderData.salesRep);
        setSalesReps(reps);
      } catch (err) {
        error(err?.response?.data?.message || 'Failed to load order');
        navigate('/offline-sales/orders');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    if (order && !order.warehouseId && warehouses.length === 1) setWarehouseId(warehouses[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, warehouses]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setIsSubmitting(true);
    try {
      const result = await editOrder(orderId);
      if (result.success) {
        success(t('offlineSales.orderUpdatedSuccess') || 'Order updated successfully');
        navigate(`/offline-sales/orders/${orderId}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || !order) {
    return (
      <div className={styles.createOfflineSalesOrderPage}>
        <div className={styles.loading}><div className={styles.spinner} /></div>
      </div>
    );
  }

  return (
    <div className={styles.createOfflineSalesOrderPage}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <ShoppingCart className={styles.headerIcon} size={22} />
            <div className={styles.titleContent}>
              <h1>{t('offlineSales.editOfflineSalesOrder') || 'Edit Offline Sales Order'} — {order.orderNumber}</h1>
              <p>{order.Shop?.name} · {order.customer?.name || t('offlineSales.cashSales')}</p>
            </div>
          </div>
          <button onClick={() => navigate(`/offline-sales/orders/${orderId}`)} className={styles.backButton}>
            <ArrowLeft size={16} />
            {t('offlineSales.backToOrders')}
          </button>
        </div>
      </div>

      <div className={styles.pageContent}>
        <form onSubmit={handleSubmit} className={styles.orderForm}>
          <div className={styles.topColumns}>
            <div className={styles.leftColumn}>
              {/* Shop selection */}
              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionHeaderLeft}>
                    <Store className={styles.sectionIcon} size={16} />
                    <h2>{t('offlineSales.shopSelection')}</h2>
                  </div>
                </div>
                <div className={styles.sectionBody}>
                  <Dropdown
                    value={shopId || ''}
                    onChange={value => setShopId(value ? parseInt(value) : null)}
                    options={shops.map(shop => ({ value: shop.id, label: shop.name }))}
                    placeholder={t('common.select')}
                    searchable
                    allowClear={false}
                    className={`${styles.fullWidthSelect} ${errors.shopId ? styles.errorDropdown : ''}`}
                  />
                  {errors.shopId && <span style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>{errors.shopId}</span>}

                  {warehouses.length > 0 && (
                    <div style={{ marginTop: '0.625rem' }}>
                      <label className={styles.fieldLabel}>{t('offlineSales.warehouse')}</label>
                      <Dropdown
                        value={warehouseId || ''}
                        onChange={value => setWarehouseId(value ? parseInt(value) : null)}
                        options={warehouses.map(warehouse => ({ value: warehouse.id, label: warehouse.name }))}
                        placeholder={t('common.select')}
                        searchable
                        allowClear={false}
                        className={styles.fullWidthSelect}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Customer */}
              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionHeaderLeft}>
                    <User className={styles.sectionIcon} size={16} />
                    <h2>{t('offlineSales.customerInformation')}</h2>
                  </div>
                </div>
                <div className={styles.sectionBody}>
                  <div className={`${styles.customerCard} ${errors.customer ? styles.customerCardError : ''}`}>
                    <div>
                      <div className={styles.customerCardName}>
                        {customer ? customer.name : t('offlineSales.cashSales')}
                      </div>
                      {customer && (
                        <div className={styles.customerCardMeta}>
                          {customer.phone || ''}
                          {customer.creditLimit != null && ` · ${t('offlineSales.creditLimit')}: ${formatCurrency(customer.creditLimit)}`}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className={styles.changeCustomerBtn}
                      onClick={() => setShowCustomerModal(true)}
                    >
                      {t('offlineSales.changeCustomer')}
                    </button>
                  </div>
                  {errors.customer && <span className={styles.errorText}>{errors.customer}</span>}
                </div>
              </div>

              {/* Sales representative */}
              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionHeaderLeft}>
                    <UserCheck className={styles.sectionIcon} size={16} />
                    <h2>{t('offlineSales.salesRep')}</h2>
                  </div>
                </div>
                <div className={styles.sectionBody}>
                  <Dropdown
                    value={salesRepUserId || ''}
                    onChange={value => setSalesRepUserId(value ? parseInt(value) : null)}
                    options={toSalesRepOptions(salesReps)}
                    placeholder={t('offlineSales.selectSalesRep')}
                    searchable
                    className={styles.fullWidthSelect}
                  />
                </div>
              </div>

              {/* Fulfillment method */}
              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionHeaderLeft}>
                    <Truck className={styles.sectionIcon} size={16} />
                    <h2>{t('offlineSales.fulfillmentMethod')}</h2>
                  </div>
                </div>
                <div className={styles.sectionBody}>
                  <div className={styles.fulfillmentOptions}>
                    {FULFILLMENT_METHODS.map(fm => {
                      const Icon = fm.icon;
                      return (
                        <button
                          type="button"
                          key={fm.key}
                          aria-pressed={fulfillmentMethod === fm.key}
                          className={`${styles.fulfillmentOption} ${fulfillmentMethod === fm.key ? styles.fulfillmentOptionActive : ''}`}
                          onClick={() => setFulfillmentMethod(fm.key)}
                        >
                          <Icon size={18} />
                          {t(`offlineSales.${fm.labelKey}`)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {fulfillmentMethod === 'courier' && (
                <div className={styles.formSection}>
                  <div className={styles.sectionHeader}>
                    <div className={styles.sectionHeaderLeft}>
                      <MapPin className={styles.sectionIcon} size={16} />
                      <h2>{t('offlineSales.deliveryDetails')}</h2>
                    </div>
                  </div>
                  <div className={styles.sectionBody}>
                    <div className={styles.formGroup} style={{ marginBottom: '0.75rem' }}>
                      <label>{t('offlineSales.deliveryAddress') || 'Delivery Address'} *</label>
                      <textarea
                        className={`${styles.notesTextarea} ${errors.address ? styles.errorInput : ''}`}
                        placeholder={t('offlineSales.deliveryAddressPlaceholder') || 'Enter the delivery address'}
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                      />
                      {errors.address && <span className={styles.errorText}>{errors.address}</span>}
                    </div>
                    <CityDeliverySection
                      cityId={cityId}
                      cities={cities}
                      error={errors.cityId}
                      isCalculatingDelivery={isCalculatingDelivery}
                      onCityChange={setCityId}
                    />
                    <DeliveryChargeWeightSection
                      weight={weight}
                      onWeightChange={setWeight}
                      deliveryCharge={totals.deliveryCharge}
                      onDeliveryChargeChange={setDeliveryChargeOverride}
                      isCalculatingDelivery={isCalculatingDelivery}
                      canChangeDeliveryCharge={hasPermission('change_delivery_charge')}
                    />
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionHeaderLeft}>
                    <FileText className={styles.sectionIcon} size={16} />
                    <h2>{t('offlineSales.notes')}</h2>
                  </div>
                </div>
                <div className={styles.sectionBody}>
                  <textarea
                    className={styles.notesTextarea}
                    placeholder={t('offlineSales.notesPlaceholder')}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className={styles.rightColumn}>
              {/* Items */}
              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionHeaderLeft}>
                    <Package className={styles.sectionIcon} size={16} />
                    <h2>{t('offlineSales.orderItems')}</h2>
                  </div>
                </div>
                <div className={styles.sectionBody}>
                  <OfflineSalesItemsSection
                    shopId={order.shopId}
                    items={items}
                    errors={errors}
                    canChangeUnitPrice={hasPermission('change_offline_sales_unit_price')}
                    canGiveDiscount={hasPermission('give_discount')}
                    onAddItem={(product) => addItem(product, product.variationId || null, product.attributeDisplay || null)}
                    onUpdateItem={updateItem}
                    onRemoveItem={removeItem}
                  />
                </div>
              </div>

              {/* Payment details — add new payments only; existing ones already show on the order detail page */}
              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionHeaderLeft}>
                    <CreditCard className={styles.sectionIcon} size={16} />
                    <h2>{t('offlineSales.paymentDetails')}</h2>
                  </div>
                </div>
                <div className={styles.sectionBody}>
                  <PaymentMethodPicker
                    methods={paymentMethods}
                    onSelect={(key) => addPaymentDetail(key)}
                    disabled={totals.balanceAmount <= 0}
                    getTitle={(method) => totals.balanceAmount <= 0
                      ? (t('ecomOrders.noRemainingBalance') || 'No remaining balance to add a payment record')
                      : method.label}
                  />
                  {paymentDetails.map((p, index) => (
                    <PaymentRecordEntry
                      key={p.id}
                      index={index}
                      payment={p}
                      methods={paymentMethods}
                      title={t('offlineSales.paymentDetails')}
                      onChange={(field, value) => updatePaymentDetail(p.id, field, value)}
                      onRemove={() => removePaymentDetail(p.id)}
                      onUploadProof={uploadOrderPaymentProof}
                      maxAmount={totals.grandTotal}
                      errors={errors.paymentDetails?.[index] || {}}
                      locked={Boolean(p.isVerified)}
                      enableCheque={!p.chequeLocked}
                      headerBadge={p.isVerified ? (
                        <span className={styles.verifiedRecordIcon} title={t('ecomOrders.verifiedPaymentRecord') || 'Verified'}>
                          <CheckCircle2 size={20} />
                        </span>
                      ) : null}
                      idPrefix={`offline-edit-${p.id}`}
                    />
                  ))}
                  {errors.creditSale && <span className={styles.errorText}>{errors.creditSale}</span>}
                </div>
              </div>

              {/* Summary */}
              <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionHeaderLeft}>
                    <FileText className={styles.sectionIcon} size={16} />
                    <h2>{t('offlineSales.orderSummary')}</h2>
                  </div>
                </div>
                <div className={styles.sectionBody}>
                  <div className={styles.summaryRow}>
                    <span>{t('offlineSales.subtotal')}</span>
                    <span>{formatCurrency(totals.subtotal)}</span>
                  </div>
                  <div className={styles.summaryRow}>
                    <span>{t('offlineSales.orderDiscount')}</span>
                    <input
                      type="number" min="0" step="0.01"
                      className={`${styles.discountInput} ${errors.orderDiscount ? styles.errorInput : ''}`}
                      value={orderDiscount}
                      onChange={(e) => setOrderDiscount(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  {fulfillmentMethod === 'courier' && totals.deliveryCharge > 0 && (
                    <div className={styles.summaryRow}>
                      <span>{t('offlineSales.deliveryCharge')}</span>
                      <span>+{formatCurrency(totals.deliveryCharge)}</span>
                    </div>
                  )}
                  <div className={`${styles.summaryRow} ${styles.summaryRowTotal}`}>
                    <span>{t('offlineSales.grandTotal')}</span>
                    <span>{formatCurrency(totals.grandTotal)}</span>
                  </div>
                  <div className={styles.summaryRow}>
                    <span>{t('offlineSales.paidAmount')}</span>
                    <span>{formatCurrency(totals.paidAmount)}</span>
                  </div>
                  {totals.balanceAmount > 0 && (
                    <div className={`${styles.summaryRow} ${styles.summaryRowBalance}`}>
                      <span>{t('offlineSales.balanceAmount')}</span>
                      <span>{formatCurrency(totals.balanceAmount)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.actionBarSpacer} />
        </form>
      </div>

      <div className={styles.fixedActionBar}>
        <div className={styles.actionBarInner}>
          <div className={styles.actionBarMeta}>
            <span className={styles.actionBarTotal}>
              {t('offlineSales.grandTotal')}: <strong>{formatCurrency(totals.grandTotal)}</strong>
            </span>
          </div>
          <div className={styles.actionBarButtons}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={() => navigate(`/offline-sales/orders/${orderId}`)}
              disabled={isSubmitting || submitting}
            >
              <X size={15} />
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className={styles.submitButton}
              onClick={handleSubmit}
              disabled={isSubmitting || submitting}
            >
              {isSubmitting || submitting ? <div className={styles.loadingSpinner} /> : (
                <>
                  <Save size={15} />
                  {t('common.save')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {showCustomerModal && (
        <CustomerSelectorModal
          current={customer}
          onSelect={(c) => { setCustomer(c); if (c?.address) setAddress(c.address); setShowCustomerModal(false); }}
          onClose={() => setShowCustomerModal(false)}
        />
      )}
    </div>
  );
};

export default EditOfflineSalesOrderPage;
